/**
 * Sandeep Sirohi
 * [09/08/2023]
 * User service for caching and getting user related information accross the application.
 * More functions can be added as per the requirement.
 */

import { msalInstance } from "..";
import { loginRequest } from "../azure/authConfig";
import httpService from "./httpService";
const CryptoJS = require("crypto-js");
var jwt = require("jsonwebtoken");

/**
 * Sandeep: encrypts the data.
 * @param data
 * @returns
 */
const AESkey = "x1e2t3e4c5h";
export const encrypt = (data: any) => {
    return data ? CryptoJS.AES.encrypt(JSON.stringify(data), AESkey).toString() : null;
}

/**
 * Sandeep: decrypts the data.
 * @param data
 * @returns
 */
export const decrypt = (data: any) => {
    var bytes = data ? CryptoJS.AES.decrypt(data, AESkey) : null;
    return (bytes ? JSON.parse(bytes.toString(CryptoJS.enc.Utf8)) : null);
}

/**
 * Sandeep: Get complete user info including setting,permission and other cached items.
 * @param key
 * @returns
 */
export const getUserInfo = () => {
    let userInfo = {
        User: getCacheItem('User'),
        UserPermission: getCacheItem('UserPermission'),
        UserSetting: getCacheItem('UserSetting'),
        SystemSetting: getCacheItem('SystemSetting'),
    }
    return userInfo;
}

/**
 * Sandeep: Get cahce value by key
 * @param key
 * @returns
 */
export const getCacheItem = (key: string) => {
    let item = sessionStorage.getItem(key);
    return decrypt(item);
}

/**
 * Sandeep: Helper methods for getting user info and cache them.
 * Extract email from the token to get user info.
 * @param callback
 */
export const initilizeUserInfo = async (callback: any) => {
    await msalInstance.acquireTokenSilent(loginRequest).then(token => {
        if (token) {
            let tokenInfo = jwt.decode(token.accessToken);
            httpService.get(setting.baseAPIUrl + `Auth/UserInfo/${tokenInfo.emails[0]}`,
                {
                    //Sandeep: Adding token and ClientId. ClientId is for Ocelot rate limiting.
                    headers: { 'Authorization': 'Bearer ' + token.accessToken, 'ClientId': tokenInfo.oid }
                }).then(res => {
                    if (res.data) {
                        sessionStorage.setItem("Token", CryptoJS.AES.encrypt(JSON.stringify(token.accessToken), AESkey).toString());
                        sessionStorage.setItem("User", CryptoJS.AES.encrypt(JSON.stringify(res.data.User), AESkey).toString());
                        sessionStorage.setItem("UserPermission", CryptoJS.AES.encrypt(JSON.stringify(res.data.UserPermission), AESkey).toString());
                        sessionStorage.setItem("UserSetting", CryptoJS.AES.encrypt(JSON.stringify(res.data.UserSetting), AESkey).toString());
                        sessionStorage.setItem("SystemSetting", CryptoJS.AES.encrypt(JSON.stringify(res.data.SystemSetting), AESkey).toString());
                        sessionStorage.setItem("LookupValue", CryptoJS.AES.encrypt(JSON.stringify(res.data.LookupValue), AESkey).toString());

                        //Sandeep: Execute the callback for further processing once cache is set.
                        if (isUserActive()) {
                            if (callback) callback(res.status);
                        }
                        else {
                            if (callback) callback("401");
                        }
                    }
                }).catch((reason) => {
                    if (callback) callback(reason && reason.response && reason.response.status);
                });
        }
    });
}

/**
 * Sandeep: Get a list of system setting, in case we want setttings to be cached without user logged in in to the system.
 * User login will override existing settings values in the cache.
 */
export const initilizeSystemSetting = async () => {
    await httpService.get(setting.baseAPIUrl + `Application/SystemSetting`).then(res => {
        if (res.data) {
            sessionStorage.setItem("SystemSetting", CryptoJS.AES.encrypt(JSON.stringify(res.data.SystemSetting), AESkey).toString());
        }
    });
}

/**
 * Sandeep: Get lookup value from the cache. 
 * @param securityObject
 * @returns
 */
export const getLookupValue = (lookupName?: string) => {
    let lookupValaue = getCacheItem("LookupValue");
    return lookupValaue && lookupValaue.filter((item: any) => item.LookupName === lookupName) || null;
}

/**
 * Sandeep: Get user permission based on security object from the cache. 
 * @param securityObject
 * @returns
 */
export const getPermission = (securityObject?: string): any | null => {
    let permission = getCacheItem("UserPermission");
    return permission && permission.length > 0 && permission.find((item: any) => item.SecurityObject === securityObject) || null;
}

/**
 * Sandep: return cached token after decrypting it.
 * @returns
 */
export const getToken = (): string | null => {
    return getCacheItem("Token");
}

/**
 * Sandep: return oid from access token.
 * @returns
 */
export const getOid = (): string | null => {
    return jwt.decode(getCacheItem("Token")).oid;
}

/**
 * Sandeep: Get logged in user id from cache.
 * @returns
 */
export const getUserId = (): number | null => {
    let userInfo = getCacheItem("User");
    return userInfo && userInfo.UserId || null;
}

/**
 * Sandeep: Get logged in user id from cache.
 * @returns
 */
export const isUserVerified = (): boolean => {
    let userInfo = getCacheItem("User");
    return userInfo && userInfo.IsVerified || null;
}

export const isUserActive = (): boolean => {
    let userInfo = getCacheItem("User");
    return userInfo && userInfo.IsActive || null;
}

/**
 * Sandeep: Get logged in user domain.
 * @returns
 */
export const getUserDomain = (): string | null => {
    let userInfo = getCacheItem("User");
    return userInfo && userInfo.Domain || null;
}

/**
 * Sandeep: Get cached setting.
 * @returns
 */
export const getSettingValue = (name: string): string | null => {
    let setting = getCacheItem("SystemSetting");
    let value = setting && setting.length > 0 && setting.find((x: any) => x.Name == name);
    return value && value.Value || null;
}

/**
 * Sandeep: Get required permission for the logged in user from cache.
 * Read,Add,Edit,Delete and Grant permission getter.
 * @param securityObject
 * @returns
 */
export const hasReadPermission = (securityObject?: string) => {
    if (!securityObject) return true;
    var permission = getPermission(securityObject);
    return permission ? permission.ReadPermission : false;
}

export const hasPaystubPermission = () => {
    let userInfo = getCacheItem("User");
    return userInfo && userInfo.HasPaystubPermission || null;
}

export const hasAddPermission = (securityObject?: string) => {
    if (!securityObject) return true;
    var permission = getPermission(securityObject);
    return permission ? permission.AddPermission : false;
}

export const hasDeletePermission = (securityObject?: string) => {
    if (!securityObject) return true;
    var permission = getPermission(securityObject);
    return permission ? permission.DeletePermission : false;
}

export const hasEditPermission = (securityObject?: string) => {
    if (!securityObject) return true;
    var permission = getPermission(securityObject);
    return permission ? permission.EditPermission : false;
}

export const hasGrantPermission = (securityObject?: string) => {
    if (!securityObject) return true;
    var permission = getPermission(securityObject);
    return permission ? permission.GrantPermission : false;
}